import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { SiteNav } from '@/components/generals/site-nav'
import { SiteFooter } from '@/components/generals/site-footer'
import { ContactForm } from '@/components/contact/contact-form'
import { ContactIndex } from '@/components/contact/contact-index'
import { revealDelay, useRevealGroup } from '@/hooks/use-reveal'

const Contact: FC = (): ReactNode => {
    const ref = useRevealGroup<HTMLDivElement>()

    return (
        <>
            <Seo
                title='Contact'
                description='Write to the studio. Prints, commissions, or something else entirely. We answer before the grain settles.'
                ogImage='/assets/reverie/rev-09-the-threshold.png'
            />

            <SiteNav />

            <div ref={ref} className='relative mx-auto flex min-h-svh w-full max-w-[var(--rev-measure)] flex-col'>
                <main className='flex-1 px-[var(--rev-gutter)] pt-[92px] pb-[clamp(60px,10vh,120px)]'>
                    <div className='flex items-baseline justify-between'>
                        <span
                            data-reveal
                            className='rev-reveal rev-eyebrow text-[color:var(--rev-ink-faint)]'
                            style={revealDelay(0)}
                        >
                            Contact · Reverie
                        </span>

                        <span
                            data-reveal
                            className='rev-reveal rev-eyebrow text-[color:var(--rev-ink-faint)]'
                            style={revealDelay(120)}
                        >
                            № 12
                        </span>
                    </div>

                    <div
                        aria-hidden='true'
                        data-reveal
                        className='rev-reveal mt-[26px] h-px w-full bg-[var(--rev-hairline)]'
                        style={revealDelay(180)}
                    />

                    <h1 className='mt-[clamp(40px,8vh,96px)]'>
                        {['Say it before', 'the grain settles.'].map((line, i) => (
                            <span
                                key={line}
                                data-reveal
                                className={`rev-reveal font-display block text-[color:var(--rev-ink)] ${i === 1 ? 'md:pl-[10%]' : ''}`}
                                style={{
                                    ...revealDelay(220 + i * 130),
                                    fontSize: 'clamp(34px, 6.4vw, 88px)',
                                    lineHeight: 0.98,
                                    fontWeight: 600,
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                {line}
                            </span>
                        ))}
                    </h1>

                    <div className='mt-[clamp(52px,10vh,120px)] grid grid-cols-1 gap-x-[clamp(16px,2.4vw,40px)] gap-y-[clamp(56px,9vh,90px)] md:grid-cols-12'>
                        <div
                            data-reveal
                            className='rev-reveal col-span-1 md:col-span-6'
                            style={revealDelay(320)}
                        >
                            <ContactForm />
                        </div>

                        <div
                            data-reveal
                            className='rev-reveal col-span-1 md:col-span-4 md:col-start-9 md:mt-[clamp(10px,3vh,40px)]'
                            style={revealDelay(440)}
                        >
                            <ContactIndex />
                        </div>
                    </div>
                </main>

                <SiteFooter />
            </div>
        </>
    )
}

export default Contact
